import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { User, Mail, Phone, Crown, LogOut, Shield, Bell, KeyRound, Vote, Gift, Share2, Copy } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const UFS = ['BR','AC','AL','AP','AM','BA','CE','DF','ES','GO','MA','MT','MS','MG','PA','PB','PR','PE','PI','RJ','RN','RS','RO','RR','SC','SP','SE','TO'];

const Perfil = () => {
  const { user, logout, isPro, loading } = useAuth();
  const navigate = useNavigate();
  const [prefs, setPrefs] = useState({ enabled: false, uf: 'BR' });
  const [savingPrefs, setSavingPrefs] = useState(false);
  const [ref, setRef] = useState(null);

  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        const r = await axios.get(`${API}/newsletter/preferences`, { withCredentials: true });
        setPrefs({
          enabled: r.data?.newsletter_pesquisas?.enabled || false,
          uf: r.data?.newsletter_pesquisas?.uf || 'BR',
        });
      } catch (_) { /* silencioso */ }
      try {
        const r = await axios.get(`${API}/referrals/me`, { withCredentials: true });
        setRef(r.data);
      } catch (_) { /* silencioso */ }
    })();
  }, [user]);

  const savePrefs = async (nextEnabled, nextUf) => {
    setSavingPrefs(true);
    try {
      await axios.post(`${API}/newsletter/pesquisas/preferences`, {
        enabled: nextEnabled,
        uf: nextUf,
      }, { withCredentials: true });
      setPrefs({ enabled: nextEnabled, uf: nextUf });
      toast.success(nextEnabled ? 'Você receberá alertas toda segunda-feira.' : 'Alertas desativados.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Não foi possível salvar.');
    } finally {
      setSavingPrefs(false);
    }
  };

  if (loading) return null;

  if (!user) {
    return (
      <div className="max-w-md mx-auto py-16 text-center" data-testid="perfil-guest">
        <div className="w-16 h-16 bg-[#E8F5E9] rounded-full flex items-center justify-center mx-auto mb-4">
          <User className="w-8 h-8 text-[#006030]" />
        </div>
        <h1 className="text-xl font-black text-[#0F1C2E] mb-2">Entre na sua conta</h1>
        <p className="text-sm text-[#475569] mb-6">
          Faça login para acompanhar candidatos, receber alertas e ver seu histórico.
        </p>
        <Button
          onClick={() => navigate('/auth')}
          className="bg-[#006030] hover:bg-[#004020] text-white"
          data-testid="perfil-goto-login"
        >
          Entrar / Criar conta
        </Button>
      </div>
    );
  }

  const initial = (user.nome || user.email || '?').slice(0, 1).toUpperCase();

  return (
    <div className="max-w-2xl mx-auto space-y-4" data-testid="perfil-page">
      <div className="text-center py-4">
        <h1 className="text-2xl md:text-3xl font-black text-[#0F1C2E] tracking-tight">
          Sua conta
        </h1>
        <p className="text-sm text-[#475569] mt-1">Gerencie seus dados e preferências</p>
      </div>

      {/* Identity card */}
      <Card className="p-5 border border-[#E2E8F0]" data-testid="perfil-identity-card">
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 rounded-full bg-[#006030] text-white flex items-center justify-center text-2xl font-black shrink-0">
            {initial}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-lg font-bold text-[#0F1C2E] truncate" data-testid="perfil-nome">
              {user.nome || 'Usuário'}
            </p>
            <div className="flex items-center gap-1 text-xs text-[#475569] mt-0.5">
              {isPro ? (
                <>
                  <Crown className="w-3.5 h-3.5 text-[#F0D000]" />
                  <span className="font-semibold">Plano PRO</span>
                </>
              ) : (
                <span>Plano Gratuito</span>
              )}
            </div>
          </div>
          {!isPro && (
            <Button
              size="sm"
              onClick={() => navigate('/planos')}
              className="bg-[#006030] hover:bg-[#004020] text-white"
              data-testid="perfil-upgrade-btn"
            >
              <Crown className="w-3.5 h-3.5 mr-1.5" /> Upgrade
            </Button>
          )}
        </div>

        <div className="mt-4 pt-4 border-t border-[#E2E8F0] space-y-2 text-sm">
          {user.email && (
            <div className="flex items-center gap-2 text-[#475569]" data-testid="perfil-email">
              <Mail className="w-4 h-4 text-[#94A3B8]" /> {user.email}
            </div>
          )}
          {user.telefone && (
            <div className="flex items-center gap-2 text-[#475569]" data-testid="perfil-telefone">
              <Phone className="w-4 h-4 text-[#94A3B8]" /> {user.telefone}
            </div>
          )}
        </div>
      </Card>

      {/* Quick actions */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Card
          className="p-4 border border-[#E2E8F0] hover:border-[#006030] cursor-pointer transition-colors"
          onClick={() => navigate('/alertas')}
          data-testid="perfil-card-alertas"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-[#E8F5E9] flex items-center justify-center">
              <Bell className="w-5 h-5 text-[#006030]" />
            </div>
            <div>
              <p className="font-semibold text-sm text-[#0F1C2E]">Meus alertas</p>
              <p className="text-xs text-[#475569]">Candidatos que você acompanha</p>
            </div>
          </div>
        </Card>

        <Card
          className="p-4 border border-[#E2E8F0] hover:border-[#006030] cursor-pointer transition-colors"
          onClick={() => navigate('/history')}
          data-testid="perfil-card-historico"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-[#E8F5E9] flex items-center justify-center">
              <Shield className="w-5 h-5 text-[#006030]" />
            </div>
            <div>
              <p className="font-semibold text-sm text-[#0F1C2E]">Histórico</p>
              <p className="text-xs text-[#475569]">Consultas anteriores</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Convide amigos e ganhe PRO */}
      {ref && (
        <Card className="p-5 border-2 border-[#006030]/40 bg-gradient-to-br from-[#F0FDF4] to-[#DCFCE7]" data-testid="perfil-referrals-card">
          <h3 className="text-sm font-black text-[#0F1C2E] mb-2 flex items-center gap-2 uppercase tracking-wider">
            <Gift className="w-4 h-4 text-[#006030]" /> Convide 3 amigos e ganhe 1 mês PRO
          </h3>
          <p className="text-xs text-[#475569] mb-3 leading-relaxed">
            {ref.unlocked
              ? 'Você já resgatou seu bônus. Continue compartilhando para espalhar transparência!'
              : `Você tem ${ref.count} de ${ref.goal} amigos convidados. Faltam ${Math.max(0, ref.goal - ref.count)} para liberar ${ref.reward_days} dias de PRO.`}
          </p>
          {/* Barra de progresso */}
          <div className="mb-3">
            <div className="flex items-center justify-between text-xs mb-1">
              <span className="font-bold text-[#0F1C2E]" data-testid="referral-progress-count">
                {ref.count} / {ref.goal}
              </span>
              {ref.unlocked && (
                <span className="text-[#006030] font-bold" data-testid="referral-unlocked-badge">🎉 PRO liberado!</span>
              )}
            </div>
            <div className="h-3 bg-white rounded-full overflow-hidden border border-[#006030]/20">
              <div
                className="h-full bg-[#006030] transition-all"
                style={{ width: `${Math.min(100, (ref.count / ref.goal) * 100)}%` }}
                data-testid="referral-progress-bar"
              />
            </div>
          </div>
          {/* Código */}
          <div className="flex items-center gap-2 mb-3">
            <div className="flex-1 bg-white border border-[#006030]/30 rounded-md px-3 py-2 font-mono text-sm font-bold text-[#0F1C2E]" data-testid="referral-code">
              {ref.code}
            </div>
            <button
              type="button"
              onClick={() => {
                navigator.clipboard.writeText(ref.share_url);
                toast.success('Link copiado.');
              }}
              className="h-10 w-10 flex items-center justify-center border border-[#006030] text-[#006030] rounded-md hover:bg-[#E8F5E9]"
              aria-label="Copiar link"
              data-testid="referral-copy-btn"
            >
              <Copy className="w-4 h-4" />
            </button>
          </div>
          {/* Botões compartilhar */}
          <div className="flex gap-2 flex-wrap">
            <a
              href={ref.whatsapp_url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex-1 min-w-[140px] inline-flex items-center justify-center gap-2 h-11 bg-[#25D366] hover:bg-[#20BC59] text-white text-sm font-bold rounded-md transition-colors"
              data-testid="referral-whatsapp-btn"
            >
              <Share2 className="w-4 h-4" /> Compartilhar no WhatsApp
            </a>
            <button
              type="button"
              onClick={async () => {
                if (navigator.share) {
                  try { await navigator.share({ title: 'Tô de Olho', text: 'Se cadastra pelo meu link:', url: ref.share_url }); } catch (_) {}
                } else {
                  navigator.clipboard.writeText(ref.share_url);
                  toast.success('Link copiado.');
                }
              }}
              className="flex-1 min-w-[140px] inline-flex items-center justify-center gap-2 h-11 border border-[#006030] text-[#006030] hover:bg-[#E8F5E9] text-sm font-bold rounded-md"
              data-testid="referral-share-btn"
            >
              <Share2 className="w-4 h-4" /> Outros apps
            </button>
          </div>
          <p className="text-[10px] text-[#94A3B8] mt-3 leading-relaxed">
            Cada amigo que se cadastrar pelo seu link conta como 1. Ao chegar em 3, o PRO é liberado automaticamente. Você recebe um e-mail de confirmação.
          </p>
        </Card>
      )}

      {/* Newsletter opt-in */}
      <Card className="p-5 border border-[#E2E8F0]" data-testid="perfil-newsletter-card">
        <h3 className="text-sm font-bold text-[#0F1C2E] mb-2 flex items-center gap-2">
          <Vote className="w-4 h-4 text-[#006030]" /> Alertas de pesquisas eleitorais
        </h3>
        <p className="text-xs text-[#475569] mb-3 leading-relaxed">
          Receba toda segunda-feira as pesquisas registradas no TSE do seu estado. Apenas dados cadastrais oficiais e link para o registro. Sem opinião política.
        </p>
        <div className="flex items-center justify-between gap-3 mb-3">
          <label htmlFor="newsletter-toggle" className="text-sm text-[#0F1C2E] font-semibold cursor-pointer">
            Receber alertas semanais
          </label>
          <button
            id="newsletter-toggle"
            type="button"
            role="switch"
            aria-checked={prefs.enabled}
            disabled={savingPrefs}
            onClick={() => savePrefs(!prefs.enabled, prefs.uf)}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${prefs.enabled ? 'bg-[#006030]' : 'bg-[#CBD5E1]'} disabled:opacity-50`}
            data-testid="newsletter-toggle"
          >
            <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${prefs.enabled ? 'translate-x-6' : 'translate-x-1'}`} />
          </button>
        </div>
        {prefs.enabled && (
          <div className="flex items-center gap-2">
            <label htmlFor="newsletter-uf" className="text-xs font-semibold text-[#475569] uppercase tracking-wider">Estado</label>
            <select
              id="newsletter-uf"
              value={prefs.uf}
              onChange={(e) => savePrefs(true, e.target.value)}
              disabled={savingPrefs}
              className="h-9 border border-[#E2E8F0] rounded-md px-2 text-sm min-w-[80px]"
              data-testid="newsletter-uf-select"
            >
              {UFS.map((u) => <option key={u} value={u}>{u === 'BR' ? 'Brasil (nacional)' : u}</option>)}
            </select>
          </div>
        )}
        <p className="text-[10px] text-[#94A3B8] mt-3">
          Enviado por Resend. Você pode desativar a qualquer momento.
        </p>
      </Card>

      {/* Security */}
      <Card className="p-5 border border-[#E2E8F0]" data-testid="perfil-security-card">
        <h3 className="text-sm font-bold text-[#0F1C2E] mb-3 flex items-center gap-2">
          <KeyRound className="w-4 h-4 text-[#006030]" /> Segurança
        </h3>
        <p className="text-xs text-[#475569] mb-3">
          Mantenha um e-mail de recuperação atualizado para não perder o acesso.
        </p>
        {user.tem_email_recuperacao === false && (
          <p className="text-xs bg-[#FEF3C7] text-[#92400E] border border-[#FDE68A] rounded px-3 py-2 mb-3">
            Você ainda não cadastrou um e-mail de recuperação.
          </p>
        )}
        <div className="flex flex-wrap gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={() => navigate('/reset-password')}
            className="border-[#E2E8F0]"
            data-testid="perfil-change-password-btn"
          >
            Redefinir senha
          </Button>
        </div>
      </Card>

      {/* Sign out */}
      <div className="pt-2">
        <Button
          variant="outline"
          onClick={logout}
          className="w-full border-[#FCA5A5] text-[#C62828] hover:bg-[#FEE2E2] hover:text-[#C62828]"
          data-testid="perfil-logout-btn"
        >
          <LogOut className="w-4 h-4 mr-2" /> Sair da conta
        </Button>
      </div>
    </div>
  );
};

export default Perfil;
